import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSiteSettings } from './SiteSettingsContext';

const CANONICAL_BASE = 'https://salonidesigns.com';

function absoluteMediaUrl(path) {
  const raw = String(path || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${CANONICAL_BASE}${raw.startsWith('/') ? '' : '/'}${raw}`;
}

const PAGE_META = {
  home: {
    path: '/',
    fallbackTitle: 'Saloni Designs | Handcrafted Rakhi, Lumba & Festive Jewelry — Shop Online',
    fallbackDescription:
      'Shop Saloni Designs — handcrafted Rakhi, Lumba, bracelets & festive jewelry. Authentic Indian designs shipped across India. Order online or visit our exhibitions.',
  },
  shop: {
    path: '/shop',
    fallbackTitle: 'Shop Handcrafted Jewelry Collections | Saloni Designs',
    fallbackDescription:
      'Browse Rakhi, Lumba, bracelets, necklaces & more. Handcrafted Indian jewelry — shipped across India.',
  },
  contact: {
    path: '/contact',
    fallbackTitle: 'Contact Saloni Designs | Handcrafted Jewelry & Rakhi',
    fallbackDescription:
      'Contact Saloni Designs for orders, custom jewelry, and enquiries. Call, email, or WhatsApp — we ship across India.',
  },
  about: {
    path: '/about',
    fallbackTitle: 'About Saloni Designs | Handcrafted Jewelry & Rakhi',
    fallbackDescription:
      'Learn about Saloni Designs — handcrafted jewelry rooted in tradition, made with care and shipped across India.',
  },
  exhibitions: {
    path: '/exhibitions',
    fallbackTitle: 'Exhibition Schedule | Saloni Designs',
    fallbackDescription:
      'Find upcoming Saloni Designs exhibitions and craft shows — browse by city and meet us in person.',
  },
  search: {
    path: '/search',
    fallbackTitle: 'Search products | Saloni Designs',
    fallbackDescription: 'Search our handcrafted catalog by product name or ID.',
  },
  blog: {
    path: '/blog',
    fallbackTitle: 'Gift & styling ideas | Saloni Designs',
    fallbackDescription: 'Tips for choosing Rakhi and Lumba — from our studio in Ahmedabad.',
  },
};

/**
 * @param {{ page: 'home'|'shop'|'contact'|'about'|'exhibitions'|'search'|'blog', children?: React.ReactNode }} props
 */
export default function PageSeoHelmet({ page, children }) {
  const { settings: site } = useSiteSettings();
  const meta = PAGE_META[page] || PAGE_META.home;
  const prefix = `seo_${page}`;

  const store = site.store_copy && typeof site.store_copy === 'object' ? site.store_copy : {};
  const title =
    String(site[`${prefix}_title`] || store[`${prefix}_title`] || '').trim() || meta.fallbackTitle;
  const description =
    String(site[`${prefix}_description`] || store[`${prefix}_description`] || '').trim() ||
    meta.fallbackDescription;
  const ogImage = absoluteMediaUrl(site[`${prefix}_og_image`] || (page === 'home' ? site.seo_home_og_image : ''));
  const canonical = `${CANONICAL_BASE}${meta.path}`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Saloni Designs" />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
      </Helmet>
      {children}
    </>
  );
}
